import postal from 'postal';
import LanguageRegionCode from '@alltrails/shared/types/LanguageRegionCode';
import mapExpand from 'images/icons/share/map-expand@2x.png';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import Trail from 'types/Trails/Trail';
import { onRoadblockedClick, buildSignupUrl } from 'utils/roadblocks';
import useUser from 'hooks/useUser';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import useToggle from '@alltrails/shared/hooks/useToggle';
import { activate } from 'utils/OptimizelyHelpers';
import SignupModal from 'components/SignupModal';
import logTrailDetailsStaticMapClicked from '@alltrails/analytics/events/logTrailDetailsStaticMapClicked';
import * as styles from './styles/styles.module.scss';

const messagingChannel = postal.channel('explore_messagingChannel');

type Props = {
  trail: Trail;
  staticMapUrl: string;
  webpStaticMapUrl: string;
  href: string;
  lat: number;
  lng: number;
  event: string;
  atRef: string;
};

const messages = defineMessages({ altExpand: { defaultMessage: 'Expand map' }, altStatic: { defaultMessage: '{name} Map' } });

const ViewFullMap = ({ trail, href, staticMapUrl, webpStaticMapUrl, lat, lng, event, atRef }: Props) => {
  const intl = useIntl();
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const [isOpen, toggle] = useToggle();

  const newHref = !user ? buildSignupUrl(href, atRef, languageRegionCode) : href;

  function handleClick(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    logTrailDetailsStaticMapClicked({ trail_id: trail.trail_id });

    if (user) {
      onRoadblockedClick(messagingChannel, event, user, href, {
        atRef,
        returnToUrl: href
      });
    } else {
      if(window.branch) {
        window.branch.track('pageview', { signupModalTest: true });
      }
      toggle();
    }
  }

  const altStatic = intl.formatMessage(messages.altStatic, {
    name: trail.name
  });

  const altExpand = intl.formatMessage(messages.altExpand);
  return (
    <div className={styles.container}>
      <SignupModal isOpen={isOpen} onRequestClose={toggle} onSuccess={toggle} redirectUrl={href} />
      <div itemProp="geo" itemScope itemType="http://schema.org/GeoCoordinates">
        <meta itemProp="latitude" content={String(lat)} className="xlate-none" />
        <meta itemProp="longitude" content={String(lng)} className="xlate-none" />
      </div>
      <a className={styles.link} href={newHref} onClick={handleClick}>
        <picture>
          {webpStaticMapUrl && <source srcSet={webpStaticMapUrl} type="image/webp" />}
          <source srcSet={staticMapUrl} type="image/png" />
          <img src={staticMapUrl} alt={altStatic} className={styles.mapImage} />
        </picture>
        <div
          className={`${styles.viewFullMap} ${languageRegionCode === LanguageRegionCode.German ? styles.germanText : ''}`}
          data-testid="viewFullMap"
        >
          <p>
            <FormattedMessage defaultMessage="View Full Map" />
          </p>
          <img src={mapExpand} className={styles.expandIcon} alt={altExpand} />
        </div>
      </a>
    </div>
  );
};

export default ViewFullMap;
